/**
 * AlertDialog: a centred, branded popup for blocking validation moments
 * (e.g. "you can't do this yet because X, here's what to do next").
 *
 * Replaces a bare error/warning toast where the operator needs a clear
 * reason plus a next step. Built on the canonical .modal-overlay +
 * .modal.modal-sm classes — no bespoke modal, no inline styles.
 *
 * Pass an `actionLabel` + `onAction` to offer a one-click route to the
 * fix (e.g. "Log a service"). With no action it's a clean acknowledge
 * dialog and the dismiss button becomes the primary call to action.
 */

export type AlertTone = 'warning' | 'error' | 'info';

interface AlertDialogProps {
  tone: AlertTone;
  title: string;
  message: string;
  /** Optional primary action that routes the operator to the fix. */
  actionLabel?: string;
  onAction?: () => void;
  /** Optional second action, shown between the primary action and dismiss. */
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
  /** Defaults to "Dismiss". */
  dismissLabel?: string;
  onClose: () => void;
}

const ICON: Record<AlertTone, string> = {
  warning: '!',
  error: '!',
  info: 'i',
};

export default function AlertDialog({
  tone,
  title,
  message,
  actionLabel,
  onAction,
  secondaryActionLabel,
  onSecondaryAction,
  dismissLabel = 'Dismiss',
  onClose,
}: AlertDialogProps) {
  const hasAction = !!actionLabel && !!onAction;
  const hasSecondary = !!secondaryActionLabel && !!onSecondaryAction;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal modal-sm"
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="alert-dialog-body">
          <span className={`alert-dialog-icon alert-dialog-icon--${tone}`} aria-hidden="true">
            {ICON[tone]}
          </span>
          <h2 className="alert-dialog-title">{title}</h2>
          <p className="alert-dialog-message">{message}</p>
        </div>
        <div className="modal-footer alert-dialog-footer">
          {hasAction && (
            <button type="button" className="btn btn-primary" onClick={onAction}>
              {actionLabel}
            </button>
          )}
          {hasSecondary && (
            <button type="button" className="btn btn-secondary" onClick={onSecondaryAction}>
              {secondaryActionLabel}
            </button>
          )}
          <button
            type="button"
            className={`btn ${hasAction || hasSecondary ? '' : 'btn-primary'}`}
            onClick={onClose}
          >
            {dismissLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
